// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { Bell, CreditCard, FileText, Megaphone, Settings2, Shield, Users } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import type { InAppNotificationItem } from '@/types'
import type { LucideIcon } from 'lucide-react'

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const s = Math.floor(diff / 1000)
  if (s < 10) return 'Just now'
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

export function categoryIcon(category: string): { Icon: LucideIcon; box: string } {
  const c = category.toLowerCase()
  const map: Record<string, { Icon: LucideIcon; box: string }> = {
    security: { Icon: Shield, box: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
    billing: { Icon: CreditCard, box: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
    team: { Icon: Users, box: 'bg-blue-500/15 text-blue-600 dark:text-blue-400' },
    documents: { Icon: FileText, box: 'bg-violet-500/15 text-violet-600 dark:text-violet-400' },
    product: { Icon: Megaphone, box: 'bg-sky-500/15 text-sky-600 dark:text-sky-400' },
    system: { Icon: Settings2, box: 'bg-surface text-ink-muted' },
    platform: { Icon: Settings2, box: 'bg-brand/15 text-brand' },
  }
  return map[c] ?? { Icon: Bell, box: 'bg-surface text-ink-muted' }
}

export function severityBar(severity: string): string {
  if (severity === 'error') return 'border-l-[3px] border-l-danger'
  if (severity === 'warning') return 'border-l-[3px] border-l-amber-500'
  if (severity === 'success') return 'border-l-[3px] border-l-emerald-500'
  return 'border-l-[3px] border-l-transparent'
}

export function InAppNotificationBody({ n }: { n: InAppNotificationItem }) {
  const { Icon, box } = categoryIcon(n.category)
  const unread = !n.readAt

  return (
    <>
      <div className={cn('mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', box)}>
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-ink leading-snug">{n.title}</p>
          {unread && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand" aria-hidden />}
        </div>
        <p className="mt-0.5 text-xs text-ink-muted leading-relaxed">{n.body}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] uppercase tracking-wide text-ink-muted">
          <span>{n.category}</span>
          {n.tenantName && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate max-w-[140px]">{n.tenantName}</span>
            </>
          )}
          {(n.source === 'platform' || n.source === 'system') && (
            <>
              <span aria-hidden>·</span>
              <span>{n.source}</span>
            </>
          )}
          <span aria-hidden>·</span>
          <span>{relativeTime(n.createdAt)}</span>
        </div>
      </div>
    </>
  )
}
